import { ELLIPSIS, TextRange, widenToWords } from "./quoteText";

export interface SelectionInElement extends TextRange {
  /** Where the selection sits on screen, for placing the button beside it. */
  rect: { top: number; bottom: number; left: number; right: number };
}

const NO_RECT = { top: 0, bottom: 0, left: 0, right: 0 };

/**
 * The page's selection as character offsets into `el`'s text; null when nothing is selected inside
 * it. A drag that starts or ends a hair outside the element still counts for the part inside it.
 */
export function selectionIn(el: HTMLElement | null): SelectionInElement | null {
  if (!el || typeof window === "undefined") return null;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  const selected = selection.getRangeAt(0);
  if (!selected.intersectsNode(el)) return null;

  const whole = document.createRange();
  whole.selectNodeContents(el);
  const range = selected.cloneRange();
  if (!el.contains(selected.startContainer)) range.setStart(whole.startContainer, whole.startOffset);
  if (!el.contains(selected.endContainer)) range.setEnd(whole.endContainer, whole.endOffset);

  const before = document.createRange();
  before.selectNodeContents(el);
  before.setEnd(range.startContainer, range.startOffset);
  const start = before.toString().length;
  const end = start + range.toString().length;
  if (end <= start) return null;
  // jsdom has no layout, so ranges there have no rect.
  const rect = typeof range.getBoundingClientRect === "function" ? range.getBoundingClientRect() : NO_RECT;
  return { start, end, rect };
}

/** Selected words of the review paragraph (whose text is `h`), widened to whole words. */
export function reviewSelection(el: HTMLElement | null, h: string): SelectionInElement | null {
  const selected = selectionIn(el);
  if (!selected) return null;
  const words = widenToWords(h, selected);
  return words ? { ...words, rect: selected.rect } : null;
}

/**
 * The token indexes a selection in the quote box covers. The box's text is an opening quote mark,
 * the tokens joined by spaces, and a closing quote mark.
 */
export function quoteSelection(
  el: HTMLElement | null,
  tokens: string[]
): { indexes: number[]; rect: SelectionInElement["rect"] } | null {
  const selected = selectionIn(el);
  if (!selected) return null;
  const indexes = tokensInRange(tokens, selected);
  return indexes.length ? { indexes, rect: selected.rect } : null;
}

/** The tokens (gaps aside) that a range of the quote box's text touches with at least one letter or digit. */
export function tokensInRange(tokens: string[], range: TextRange): number[] {
  const indexes: number[] = [];
  let at = 1;
  tokens.forEach((token, i) => {
    const from = Math.max(at, range.start);
    const to = Math.min(at + token.length, range.end);
    if (to > from && token !== ELLIPSIS && /[\p{L}\p{N}]/u.test(token.slice(from - at, to - at))) indexes.push(i);
    at += token.length + 1;
  });
  return indexes;
}
